import React, {ChangeEvent, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Grid, Pagination, Popover, Table as AntTable, Tag, Tooltip} from 'antd';
import {
    CalendarBlankIcon,
    CaretDownIcon,
    CaretRightIcon,
    ClipboardTextIcon,
    FlagBannerIcon,
    FunnelSimpleIcon,
    MapPinIcon,
    TrendUpIcon,
    UserCircleIcon,
} from '@phosphor-icons/react';
import {SitesHeader, SitesTableSearchBar} from '../../../index';
import siteService from '../../../../services/siteService';
import {useViewerStore} from '../../../../store/store';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import Filter from '../Filter/Filter';
import {getDateinMM_DD_YYYY} from '../../../../helpers/dateHelper';
import {ThemeColorContext} from '../../../../contexts/ThemeColorContext';
import { logger } from '../../../../services/loggingService';
import { DEFAULT_DATE_FROM } from "../../../../Constants";
import type { ColumnType } from "antd/es/table";
import { SiteRow } from '../../../../@types/types';
import { AlertType } from '../../../../@types/enums';
import { ViewerState } from '../../../../@types/store';
import { persistDashboardLayoutVariant } from '../../../../helpers/dashboardLayoutVariant';
import { persistSiteContext, siteRowToContext } from '../../../../pre-contract/lib/preContractTypes';
import { clearPreContractReturn } from '../../../../pre-contract/lib/preContractLaunch';
import { clearPreContractAiOptionsCache } from '../../../../pre-contract/lib/preContractAiDesigns';
import { useCurrentUser } from '../../../../contexts/UserContext';

const SITES_TABLE_SCROLL_Y_MIN = 120;
const SITES_TABLE_HEADER_FALLBACK = 52;

const SitesTable: React.FC = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tableScrollRegionRef = useRef<HTMLDivElement>(null);
    const [tableScrollY, setTableScrollY] = useState(() =>
        Math.max(SITES_TABLE_SCROLL_Y_MIN, (typeof window !== 'undefined' ? window.innerHeight : 800) - 260),
    );

    const {primary} = useContext(ThemeColorContext);
    const { user, hasCapability } = useCurrentUser();
    const isAdminUser = user?.application_role_name === 'Admin';
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isCompactTable = !screens.lg;

    //Store
    const stageSelectedFilter = useViewerStore(state => state.stageSelectedFilter);
    const dateSelectedFilter = useViewerStore(state => state.dateSelectedFilter);
    const isDateFilterApplied = useViewerStore(state => state.isDateFilterApplied);
    const isStageFilterApplied = useViewerStore(state => state.isStageFilterApplied);
    const isAuroraOnlyFilter = useViewerStore(state => state.isAuroraOnlyFilter);
    const isAppliedFilter = useViewerStore(state => state.isApplyFilter);

    //Local States
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(parseInt(urlParams.get('page') ?? '1', 10) ?? 1);
    const [tableLoading, setTableLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [siteData, setSiteData] = useState([]);

    const [tableParams, setTableParams] = useState({
        pagination: {
            current: page,
            pageSize: 0,
            total: 0,
        },
    });

    const getStageChipClass = (stageValue?: string) => {
        const txt = String(stageValue || '').toLowerCase();
        if (txt.includes('closed') || txt.includes('won') || txt.includes('complete')) {
            return 'sites-stage-chip sites-stage-chip--success';
        }
        if (txt.includes('cancel') || txt.includes('lost') || txt.includes('error')) {
            return 'sites-stage-chip sites-stage-chip--danger';
        }
        if (txt.includes('appointment') || txt.includes('pending') || txt.includes('set')) {
            return 'sites-stage-chip sites-stage-chip--warning';
        }
        return 'sites-stage-chip sites-stage-chip--info';
    };

    const normalizeDisplayText = (value: unknown): string => {
        const raw = String(value ?? '').trim();
        if (!raw) return '';
        return raw.replace(/\s+/g, ' ');
    };

    const formatOpportunityName = (value: unknown): string => {
        const normalized = normalizeDisplayText(value);
        if (!normalized) return '';
        const trimmed = normalized.replace(/\s*-\s*$/g, '');
        if (trimmed.includes(',')) {
            const [lastNamePart, firstNamePart] = trimmed.split(',', 2);
            const first = normalizeDisplayText(firstNamePart);
            const last = normalizeDisplayText(lastNamePart);
            return normalizeDisplayText(`${first} ${last}`);
        }
        return trimmed.replace(/,/g, ' ');
    };

    //Columns for the table
    const columns: ColumnType<SiteRow>[] = [
        {
            title: (
                <span className='sites-col-title sites-col-title--address'>
                    <MapPinIcon size={14} weight='fill' />
                    Address
                </span>
            ),
            dataIndex: "address",
            key: "address",
            width: 360,
            fixed: 'left',
            onHeaderCell: () => ({ className: 'sites-col-header sites-col-header--address' }),
            render: (_text, record) => (
                <a
                    onClick={() => handleEditClick(record)}
                    className='sites-table__address-link'
                    style={{ padding: 0, cursor: "pointer" }}
                >
                    {record.address}
                </a>
            ),
        },
        {
            title: (
                <span className='sites-col-title sites-col-title--multiplier'>
                    <TrendUpIcon size={14} weight='bold' />
                    Multiplier
                </span>
            ),
            dataIndex: 'multiplier',
            key: 'multiplier',
            width: '8%',
            onHeaderCell: () => ({ className: 'sites-col-header sites-col-header--multiplier' })

        },
        {
            title: (
                <span className='sites-col-title sites-col-title--opportunity'>
                    <UserCircleIcon size={14} weight='duotone' />
                    Opportunity Name
                </span>
            ),
            dataIndex: 'opportunityName',
            key: 'opportunityName',
            onHeaderCell: () => ({ className: 'sites-col-header sites-col-header--opportunity' })

        },
        ...(hasCapability('Pre_Contract_Column')
            ? ([
                  {
                      title: (
                          <span className='sites-col-title sites-col-title--pre-contract'>
                              <ClipboardTextIcon size={14} weight='duotone' />
                              Pre-Contract
                          </span>
                      ),
                      key: 'preContract',
                      width: 140,
                      fixed: 'right',
                      onHeaderCell: () => ({
                          className: 'sites-col-header sites-col-header--pre-contract',
                      }),
                      render: (_text, record) => (
                          <Button
                              type="link"
                              className="sites-pre-contract-btn"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreContractClick(record);
                              }}
                          >
                              Start setup
                          </Button>
                      ),
                  },
              ] as ColumnType<SiteRow>[])
            : []),
        {
            title: (
                <span className='sites-col-title sites-col-title--stage'>
                    <FlagBannerIcon size={14} weight='fill' />
                    Stage
                </span>
            ),
            dataIndex: 'stage',
            key: 'stage',
            width: 220,
            fixed: 'right',
            onHeaderCell: () => ({ className: 'sites-col-header sites-col-header--stage' }),
            render: (text) => (
                <Tag className={getStageChipClass(text)}>
                    {text}
                </Tag>
            ),
        },
        {
            title: (
                <span className='sites-col-title sites-col-title--created'>
                    <CalendarBlankIcon size={14} weight='duotone' />
                    Created Date
                </span>
            ),
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 160,
            fixed: 'right',
            onHeaderCell: () => ({ className: 'sites-col-header sites-col-header--created' }),
            render: (text) => <span>{getDateinMM_DD_YYYY(text).date}</span>,
        },
    ];
    const responsiveColumns: ColumnType<SiteRow>[] = columns.map((column) => ({
        ...column,
        fixed: isCompactTable ? undefined : column.fixed,
    }));

    //functionality for storing the searched data
    const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);

        if (!isDateFilterApplied) {
            if (value) {
                useViewerStore.setState({
                    dateSelectedFilter: {
                        dateFrom: DEFAULT_DATE_FROM,
                        dateTo: null
                    }
                });
            } else {
                useViewerStore.setState({
                    dateSelectedFilter: {
                        dateFrom: DEFAULT_DATE_FROM,
                        dateTo: null
                    }
                });
            }
        }
    }

    const getForamattedData = (data: any) => {
       return data.Data.map((item: any, index: number) => {
            const [state, zipCode] = item.State?.split('-') ?? [];
            const address = normalizeDisplayText(item.Address) || 'No Address';
            const opportunityName = formatOpportunityName(item.Opportunity_Name) || 'No Opportunity Name';

            return {
                key: index,
                address,
                opportunityName,
                salesPerson: item.SalesPerson,
                stage: item.Stage,
                createdDate: item.CreatedDate,
                state: state || '',
                zipCode: zipCode || '',
                directLead: item.DirectLead,
                account_Id: item.Account_Id,
                salesForceId: item.SalesForceId,
                multiplier: item.Multiplier,
                data_Source: item.Data_Source
            }
        });
    }

    const search = async (query: string, page: number) => {
        setTableLoading(true);

        try {
            const data = await siteService.postSitesSearch(
                searchValue,
                page,
                stageSelectedFilter,
                dateSelectedFilter,
                isDateFilterApplied,
                isStageFilterApplied,
                isAuroraOnlyFilter,
            );
            if (data.Message === 'No records found') {
                setSiteData([]);
                setTableParams({
                    pagination: {
                        current: 1,
                        pageSize: 0,
                        total: 0,
                    },
                });
                return;
            }

            setSiteData(getForamattedData(data));

            setTableParams({
                pagination: {
                    current: data.PageNumber,
                    pageSize: data.PageSize,
                    total: data.TotalRecords,
                }
            });
        } catch (error: any) {
            logger.error(error);
            if (error.response && (error.response.status >= 400 && error.response.status < 600)) {
                setSiteData([]);
                setTableParams({
                    pagination: {
                        current: 1,
                        pageSize: 0,
                        total: 0,
                    },
                });
            }
        } finally {
            setTableLoading(false);
        }
    }

    //functionality for handling the search bar
    const handleSearch = () => {
        search(searchValue.trim(), 1);
    };

    const handlePageChange = (page: number) => {
        const url = new URL(window.location.href);
        url.searchParams.set('page', page.toString());
        window.history.pushState({}, '', url);
        setPage(page);
    }

    //functionality that triggers the api call at the beginning
    useEffect(() => {
        search(searchValue.trim(), page);
    }, [page, isAppliedFilter]);

    const handleEditClick = (data: SiteRow) => {
        sessionStorage.setItem('SalesForceId', data.salesForceId);
        sessionStorage.setItem('currentStage', String(parseInt(data.stage.split(':')[0])));
        // Clear stale OneButton state so previous opportunity data doesn't show up in the editor.
        useOneButtonStore.getState().setSalesForceId(String(data.salesForceId));
        useViewerStore.setState({
            treeCartItems : [], 
            pathname : '/configure/roof', 
            viewerAlert: {
                show: false,
                title: "",
                message: "",
                messageType: AlertType.info,
                isModal: false
            },
            isStreetViewVisible:false,
            loadingCompleted: false,
            siteLoadingError: null,
            siteMultipliersPendingNotice: null,
        });
        //Navigating to the editor page
        navigate(`/configure?sfid=${data.salesForceId}`);
    }

    const handlePreContractClick = (data: SiteRow) => {
        const ctx = siteRowToContext(data);
        persistSiteContext(ctx);
        persistDashboardLayoutVariant('v2');
        clearPreContractReturn();
        clearPreContractAiOptionsCache();
        useViewerStore.setState({ dashboardLayoutVariant: 'v2' });
        useOneButtonStore.getState().setSalesForceId(String(data.salesForceId));
        navigate(`/pre-contract?sfid=${data.salesForceId}`, { state: { site: data } });
    }


    const handleOpenChange = (newOpen: boolean) => {setOpen(newOpen);};

    const handleResetSearch = () => {
        setSearchValue('');
        useViewerStore.setState({
            stageSelectedFilter: [],
            dateSelectedFilter: {
                dateFrom: DEFAULT_DATE_FROM,
                dateTo: null
            },
            isStageFilterApplied: false,
            isDateFilterApplied: false,
            isAuroraOnlyFilter: false,
        })
        useViewerStore.setState((prev: ViewerState) => ({isApplyFilter: !prev.isApplyFilter}));
    }
    useEffect(()=>{
        document.title = `TS-OneDraw Sites Table `;
        useViewerStore.setState({isLeftSidebarOpen:false});
    },[])

    useLayoutEffect(() => {
        const region = tableScrollRegionRef.current;
        if (!region) return;

        const measure = () => {
            const h = region.clientHeight;
            if (h <= 0) return;
            const headerEl = region.querySelector('.ant-table-header') as HTMLElement | null;
            const headerH = headerEl?.getBoundingClientRect().height ?? SITES_TABLE_HEADER_FALLBACK;
            const y = Math.floor(h - headerH - 2);
            setTableScrollY(y > SITES_TABLE_SCROLL_Y_MIN ? y : SITES_TABLE_SCROLL_Y_MIN);
        };

        measure();
        const ro = new ResizeObserver(() => measure());
        ro.observe(region);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [tableLoading]);

    return (
        <div className='sites-page'>
            <SitesHeader>
                <SitesTableSearchBar
                    name='search'
                    placeholder='Search...'
                    value={searchValue}
                    onSearch={handleSearch}
                    onReset={handleResetSearch}
                    onChange={(e) => onSearchChange(e)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleSearch();
                        }
                    }}
                />

                
                    <Popover
                        overlayClassName='sites-filter-popover'
                        content={<Filter/>}
                        title='Filter Options'
                        trigger='click'
                        open={open}
                        onOpenChange={handleOpenChange}
                    >
                        <Button
                            className='sites-filter-btn'
                            type='primary'
                            size='large'
                            icon={<FunnelSimpleIcon size={24}/>}
                        >
                            Filter
                        </Button>
                    </Popover>
            </SitesHeader>

            <div className='sites-table-scroll-region' ref={tableScrollRegionRef}>
                <AntTable
                rowKey={record => record.key}
                className={'sites-table'}
                loading={tableLoading}
                dataSource={siteData}
                scroll={
                    {
                        y: tableScrollY,
                        x: 'max-content'
                    }
                }
                bordered
                columns={responsiveColumns}
                pagination={false}
                size='middle'
                rowClassName='sites-table__row'
                expandable={{
                    expandedRowRender: (record) =>
                        <ul className='sitesTableSubList'>
                            <li>
                                <span>Salesperson: {record.salesPerson}</span>
                            </li>
                            <li>
                                <span>Salesforce ID: {record.salesForceId}</span>
                            </li>
                            <li>
                                <span>Account ID: {record.account_Id}</span>
                            </li>
                            <li>
                                <span>Data Source: {record.data_Source}</span>
                            </li>
                        </ul>
                    ,
                    rowExpandable: (record) => record.name !== 'Not Expandable',
                    expandIcon: ({ expanded, onExpand, record }) => (
                        <button
                            type='button'
                            aria-label={expanded ? 'Collapse row details' : 'Expand row details'}
                            onClick={(e) => onExpand(record, e)}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 20,
                                height: 20,
                                padding: 0,
                                cursor: 'pointer',
                                color: 'var(--primary)',
                            }}
                        >
                            {expanded ? (
                                <CaretDownIcon size={14} weight='bold' />
                            ) : (
                                <CaretRightIcon size={14} weight='bold' />
                            )}
                        </button>
                    ),
                }}
                locale={
                    {
                        emptyText: tableLoading ? (
                            <div className='app__unauthorized' style={{ background: 'transparent' }}>
                                Loading...
                            </div>
                        ) : 'No Records Found'
                    }
                }

                />
            </div>

            {tableParams.pagination.total > 0 && (
                <div className='sites-table__pagination-wrap'>
                    <Pagination
                        className='sites-table__pagination'
                        responsive
                        onChange={handlePageChange}
                        showTotal={total => `Current Page Records :
                        ${tableParams.pagination.pageSize}
                        & Total Records : ${total}`}
                        showSizeChanger={false}
                        defaultCurrent={page}
                        pageSize={tableParams.pagination.pageSize}
                        total={tableParams.pagination.total}
                    />
                </div>
            )}
        </div>
    );
}

export default SitesTable;
